/**
 * Single localStorage-backed record for everything in packages/schema/persist.ts.
 *
 * The contract defines ONE unified store (`PersistedState`, keyed under one
 * `v`), but the UI is more convenient to work with as two zustand hooks —
 * `useProgressStore` and `useSettingsStore` — so components don't churn.
 * Both stores read/write through the helpers here, each touching only its
 * own slice of the unified shape so one store's write never clobbers the
 * other's data on disk.
 */

import type { EntryId } from "@schema/catalog.ts";
import {
  EMPTY_STATE,
  type PersistedSettings,
  type PersistedState,
  parsePersistedState,
  type Tombstones,
  type WatchRecord,
} from "@schema/persist.ts";
import { PROJECT_LEGACY_STORAGE_KEYS, PROJECT_STORAGE_KEY } from "@/lib/project.ts";

/**
 * The localStorage namespace, and the keys this project used before it.
 *
 * Both come from `data/project.yaml` via the catalog, so a project that
 * renames itself does not silently orphan everyone's watched progress. That is
 * the entire reason `legacyStorageKeys` exists: changing the key is a rename,
 * not a reset, and the read path below migrates the first legacy key it finds.
 *
 * Read from the generated constants rather than the catalog object, because
 * persistence has to work before the catalog has loaded — and during SSR,
 * where there is no catalog at all.
 */
const STORAGE_KEY = PROJECT_STORAGE_KEY;
const LEGACY_KEYS = PROJECT_LEGACY_STORAGE_KEYS;

/**
 * Move a previous key's value under the current one, once.
 *
 * The old key is deliberately left in place: a user who opens an older build
 * of the app, or rolls back a deploy, still finds their progress where that
 * build looks for it. Cleaning up is a later release's job, not this read's.
 */
function migrateLegacyKey(store: Storage): string | null {
  for (const key of LEGACY_KEYS) {
    const legacy = store.getItem(key);
    if (legacy !== null) {
      store.setItem(STORAGE_KEY, legacy);
      return legacy;
    }
  }
  return null;
}

/**
 * `localStorage`, or `null` wherever it isn't usable: server-side rendering,
 * Safari's private mode (which throws on access), a browser with site data
 * blocked, a test runner. The app is local-first, not local-only — losing
 * persistence should cost you your saved progress, never the page.
 */
function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Read the full unified state from localStorage, migrating and validating as
 * needed. Falls back to {@link EMPTY_STATE} when there's nothing stored yet,
 * or when what's stored can't be parsed (corrupt data shouldn't brick the app).
 */
export function readPersistedState(): PersistedState {
  const store = storage();
  let raw = store?.getItem(STORAGE_KEY) ?? null;
  if (raw === null && store) raw = migrateLegacyKey(store);
  if (raw === null) return EMPTY_STATE;
  try {
    return parsePersistedState(raw);
  } catch {
    return EMPTY_STATE;
  }
}

function writePersistedState(state: PersistedState): void {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Full quota or a blocked store: the session keeps working in memory.
  }
}

/**
 * Persist `watched` together with its tombstones, preserving `settings`.
 *
 * The two travel as one write on purpose: an un-watch removes a record AND
 * adds a tombstone, and persisting either alone leaves a state that says the
 * opposite of what happened — the entry back with no note of the deletion, or
 * a deletion for an entry that is still there.
 */
export function writeWatched(watched: Record<EntryId, WatchRecord>, deleted: Tombstones): void {
  const current = readPersistedState();
  writePersistedState({ ...current, watched, deleted });
}

/** Persist a new `settings` slice, preserving whatever `watched` is currently on disk. */
export function writeSettings(settings: PersistedSettings): void {
  const current = readPersistedState();
  writePersistedState({ ...current, settings });
}

/**
 * Replace the whole record at once. Used only by sync, which produces both
 * slices together from a merge and must not write them in two steps — a
 * half-applied merge is a state neither device ever had.
 */
export function writeWholeState(state: PersistedState): void {
  writePersistedState(state);
}
